// データ永続化レイヤー（localStorage → 後でDB切り替え可能）

export interface UserProfile {
  id: string;
  username: string;
  createdAt: string;
}

export interface StyleModel {
  userId: string;
  systemPrompt: string;
  exampleCount: number;
  createdAt: string;
  exampleTweets?: string[]; // 実際のツイート例（追加）
  frequentPhrases?: string[]; // 頻出表現（追加）
  toneAnalysis?: string; // トーン分析（追加）
}

export interface BuzzModel {
  userId: string;
  insights: string[];
  analyzedTweetCount: number;
  thresholdEngagement: number;
  createdAt: string;
}

export interface GenerationHistory {
  id: string;
  userId: string;
  inputText: string;
  selectedTweet: string;
  createdAt: string;
}

// 新しい統一HistoryItem型
export type FeedbackRating = 'banged' | 'normal' | 'flopped' | null;

export interface HistoryItem {
  id: string;
  type: 'tweet' | 'analysis' | 'coaching' | 'reply' | 'inspiration' | 'research';
  content: string;
  timestamp: string;
  score?: number;
  feedback?: FeedbackRating;
  engagementLikes?: number;
  metadata?: {
    inputText?: string;
    tone?: string;
    keyword?: string;
    [key: string]: any;
  };
}

export interface Inspiration {
  id: string;
  topic: string;
  ideas: string[];
  timestamp: string;
}

export interface ResearchResult {
  id: string;
  accountName?: string;
  analyzedCount: number;
  commonPatterns: string[];
  toneAnalysis: string;
  topicBreakdown: Record<string, number>;
  engagementTriggers: string[];
  timestamp: string;
}

export interface SavedIdea {
  id: string;
  text: string;
  folder: string;
  createdAt: string;
}

export interface IdeaFolder {
  id: string;
  name: string;
}

export const storageService = {
  // 初期化（マイグレーション実行）
  initializeStorage(): void {
    const oldKey = 'generation_history';
    const oldData = localStorage.getItem(oldKey);

    if (oldData) {
      try {
        const oldHistory = JSON.parse(oldData);
        const newHistory: HistoryItem[] = oldHistory.map((item: any) => ({
          id: item.id || Date.now().toString() + Math.random().toString(36).slice(2),
          type: 'tweet' as const,
          content: (item.selectedTweet || item.inputText || '').slice(0, 800),
          timestamp: item.createdAt || new Date().toISOString(),
          metadata: {
            inputText: item.inputText?.slice(0, 200),
          },
        }));

        const existing = localStorage.getItem('bangerfy_history');
        if (!existing) {
          localStorage.setItem('bangerfy_history', JSON.stringify(newHistory));
        }
        localStorage.removeItem(oldKey);
      } catch (err) {
        console.error('Migration failed:', err);
      }
    }
  },

  // Style Model
  getStyleModel(): StyleModel | null {
    const data = localStorage.getItem('style_model');
    if (!data) return null;
    try { return JSON.parse(data); } catch { return null; }
  },

  setStyleModel(model: StyleModel): void {
    localStorage.setItem('style_model', JSON.stringify(model));
  },

  // Buzz Model
  getBuzzModel(): BuzzModel | null {
    const data = localStorage.getItem('buzz_model');
    if (!data) return null;
    try { return JSON.parse(data); } catch { return null; }
  },

  setBuzzModel(model: BuzzModel): void {
    localStorage.setItem('buzz_model', JSON.stringify(model));
  },

  // History（統一）
  getHistoryItems(): HistoryItem[] {
    const data = localStorage.getItem('bangerfy_history');
    if (!data) return [];
    try { return JSON.parse(data); } catch { return []; }
  },

  addHistoryItem(item: Omit<HistoryItem, 'id' | 'timestamp'>): void {
    const history = this.getHistoryItems();
    const newItem: HistoryItem = {
      ...item,
      id: Date.now().toString() + Math.random().toString(36).slice(2),
      timestamp: new Date().toISOString(),
    };
    history.unshift(newItem);
    localStorage.setItem('bangerfy_history', JSON.stringify(history.slice(0, 100)));
  },

  deleteHistoryItem(id: string): HistoryItem[] {
    const items = this.getHistoryItems().filter((item) => item.id !== id);
    localStorage.setItem('bangerfy_history', JSON.stringify(items));
    return items;
  },

  setFeedback(id: string, feedback: FeedbackRating, likes?: number): void {
    const items = this.getHistoryItems();
    const index = items.findIndex((i) => i.id === id);
    if (index !== -1) {
      items[index].feedback = feedback;
      if (likes !== undefined) items[index].engagementLikes = likes;
      localStorage.setItem('bangerfy_history', JSON.stringify(items));
    }
  },

  getFeedbackItems(rating: FeedbackRating): HistoryItem[] {
    return this.getHistoryItems().filter(
      (i) => i.type === 'tweet' && i.feedback === rating
    );
  },

  clearHistory(): void {
    localStorage.removeItem('bangerfy_history');
  },

  // Saved Ideas（Saved Ideasサイドバー用）
  getSavedIdeas(): SavedIdea[] {
    const data = localStorage.getItem('bangerfy_saved_ideas');
    if (!data) return [];
    try { return JSON.parse(data); } catch { return []; }
  },

  addSavedIdea(text: string, folder: string = 'default'): void {
    const ideas = this.getSavedIdeas();
    const newIdea: SavedIdea = {
      id: Date.now().toString() + Math.random().toString(36).slice(2),
      text,
      folder,
      createdAt: new Date().toISOString(),
    };
    ideas.unshift(newIdea);
    localStorage.setItem('bangerfy_saved_ideas', JSON.stringify(ideas));
  },

  removeSavedIdea(id: string): void {
    const ideas = this.getSavedIdeas().filter((i) => i.id !== id);
    localStorage.setItem('bangerfy_saved_ideas', JSON.stringify(ideas));
  },

  updateSavedIdea(id: string, updates: Partial<SavedIdea>): void {
    const ideas = this.getSavedIdeas();
    const index = ideas.findIndex((i) => i.id === id);
    if (index !== -1) {
      ideas[index] = { ...ideas[index], ...updates };
      localStorage.setItem('bangerfy_saved_ideas', JSON.stringify(ideas));
    }
  },

  // Folders
  getFolders(): IdeaFolder[] {
    const data = localStorage.getItem('bangerfy_folders');
    let folders: IdeaFolder[] = [];
    if (data) { try { folders = JSON.parse(data); } catch { /* ignore */ } }

    const locale = localStorage.getItem('bangerfy_locale') || 'en';

    if (folders.length === 0) {
      const defaultFolder: IdeaFolder = { id: 'default', name: locale === 'ja' ? 'デフォルト' : 'Default' };
      folders.push(defaultFolder);
      localStorage.setItem('bangerfy_folders', JSON.stringify(folders));
    }

    // Keep default folder name in sync with locale
    const def = folders.find(f => f.id === 'default');
    if (def) {
      def.name = locale === 'ja' ? 'デフォルト' : 'Default';
    }

    return folders;
  },

  addFolder(name: string): void {
    const folders = this.getFolders();
    const newFolder: IdeaFolder = {
      id: Date.now().toString() + Math.random().toString(36).slice(2),
      name,
    };
    folders.push(newFolder);
    localStorage.setItem('bangerfy_folders', JSON.stringify(folders));
  },

  removeFolder(id: string): void {
    if (id === 'default') return;
    const folders = this.getFolders().filter((f) => f.id !== id);
    localStorage.setItem('bangerfy_folders', JSON.stringify(folders));

    const ideas = this.getSavedIdeas().map((i) =>
      i.folder === id ? { ...i, folder: 'default' } : i
    );
    localStorage.setItem('bangerfy_saved_ideas', JSON.stringify(ideas));
  },

  renameFolder(id: string, newName: string): void {
    const folders = this.getFolders();
    const index = folders.findIndex((f) => f.id === id);
    if (index !== -1) {
      folders[index].name = newName;
      localStorage.setItem('bangerfy_folders', JSON.stringify(folders));
    }
  },

  // Inspiration
  getInspirations(): Inspiration[] {
    const data = localStorage.getItem('bangerfy_inspirations');
    if (!data) return [];
    try { return JSON.parse(data); } catch { return []; }
  },

  addInspiration(inspiration: Omit<Inspiration, 'id' | 'timestamp'>): void {
    const inspirations = this.getInspirations();
    const newInspiration: Inspiration = {
      ...inspiration,
      id: Date.now().toString() + Math.random().toString(36).slice(2),
      timestamp: new Date().toISOString(),
    };
    inspirations.unshift(newInspiration);
    localStorage.setItem('bangerfy_inspirations', JSON.stringify(inspirations.slice(0, 50)));
  },

  clearInspirations(): void {
    localStorage.removeItem('bangerfy_inspirations');
  },

  // Research
  getResearchResults(): ResearchResult[] {
    const data = localStorage.getItem('bangerfy_research');
    if (!data) return [];
    try { return JSON.parse(data); } catch { return []; }
  },

  addResearchResult(result: Omit<ResearchResult, 'id' | 'timestamp'>): void {
    const results = this.getResearchResults();
    const newResult: ResearchResult = {
      ...result,
      id: Date.now().toString() + Math.random().toString(36).slice(2),
      timestamp: new Date().toISOString(),
    };
    results.unshift(newResult);
    localStorage.setItem('bangerfy_research', JSON.stringify(results.slice(0, 20)));
  },

  clearResearchResults(): void {
    localStorage.removeItem('bangerfy_research');
  },
};
